import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, SectionLabel, MaskedLine, LazyImage3D } from "@/components/Reveal";
import { PROJECTS } from "@/data/projects";

// Pinterest-style height cycle — carefully balanced so 4 columns end at
// similar heights. In a 4-col layout, adjacent pairs share a column:
// col1=(0,1), col2=(2,3), col3=(4,5), col4=(6,7). Each pair ≈ 1040px (desktop).
const MASONRY_HEIGHTS = [
    "h-[460px] md:h-[600px]", // 0 · Monsoon Villa
    "h-[420px] md:h-[540px]", // 1 · Casa Terra
    "h-[380px] md:h-[500px]", // 2 · Rithanya
    "h-[440px] md:h-[600px]", // 3 · Vishnudeth
    "h-[400px] md:h-[500px]", // 4 · Golden Age
    "h-[420px] md:h-[560px]", // 5 · Sabu CS
    "h-[440px] md:h-[600px]", // 6 · Ground Gallery
    "h-[360px] md:h-[460px]", // 7 · Hashim
    "h-[440px] md:h-[600px]", // 8 · Posh Ceramics
    "h-[420px] md:h-[560px]", // 9 · Pyare Jewellery
];

function ProjectCard({ project, index, heightClass }) {
    const ref = useRef(null);
    const navigate = useNavigate();
    const rx = useSpring(useMotionValue(0), { stiffness: 140, damping: 22 });
    const ry = useSpring(useMotionValue(0), { stiffness: 140, damping: 22 });

    const onMove = (e) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        ry.set(((e.clientX - rect.left) / rect.width - 0.5) * 6);
        rx.set(((e.clientY - rect.top) / rect.height - 0.5) * -5);
    };

    return (
        <FadeUp
            delay={(index % 3) * 0.08}
            y={60}
            className="block break-inside-avoid"
        >
            <motion.article
                ref={ref}
                data-testid={`project-card-${project.title.toLowerCase().replace(/\s/g, "-")}`}
                onMouseMove={onMove}
                onMouseLeave={() => {
                    rx.set(0);
                    ry.set(0);
                }}
                onClick={() => navigate(`/portfolio/${project.slug}`)}
                style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
                className={`group relative w-full cursor-pointer overflow-hidden rounded-2xl border border-white/[0.06] ${heightClass}`}
                data-cursor="hover"
                role="link"
                aria-label={`Open ${project.title} case study`}
            >
                <LazyImage3D
                    src={project.img}
                    alt={`${project.title} — ${project.category}, ${project.location}`}
                    className="!absolute inset-0 h-full w-full"
                    imgClassName="transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]"
                    style={project.imgPosition ? { objectPosition: project.imgPosition } : undefined}
                    rotateX={index % 2 === 0 ? 14 : -14}
                    rotateY={index % 2 === 0 ? -8 : 8}
                    scale={0.88}
                    y={80}
                    delay={index * 0.05}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-coal/95 via-coal/20 to-transparent opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute inset-0 bg-gold/0 transition-colors duration-700 group-hover:bg-gold/[0.06]" />

                <div className="absolute right-5 top-5 flex h-11 w-11 translate-y-2 items-center justify-center rounded-full border border-white/20 bg-coal/40 opacity-0 backdrop-blur-md transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <ArrowUpRight size={16} className="text-gold" />
                </div>

                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
                    <p className="text-[10px] font-medium uppercase tracking-[0.35em] text-gold">
                        {project.category} · {project.year}
                    </p>
                    <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-white sm:text-2xl lg:text-3xl">
                        {project.title}
                    </h3>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-ash">
                        {project.location}
                    </p>
                </div>
            </motion.article>
        </FadeUp>
    );
}

export default function Portfolio() {
    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="relative mx-auto max-w-[1500px] overflow-clip px-6 py-32 sm:px-10 sm:py-44"
        >
            <SectionLabel index="04" title="Selected Works" />
            <h2 className="mt-10 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                <MaskedLine delay={0.05}>Proof, not promises —</MaskedLine>
                <MaskedLine delay={0.15}>
                    <span className="text-gold">built to be lived in.</span>
                </MaskedLine>
            </h2>
            <FadeUp delay={0.25} className="mt-8 max-w-xl">
                <p className="text-sm leading-relaxed text-ash sm:text-base">
                    Eleven featured projects across Kerala — private
                    villas in Kundara, Pandikkad, Kavanadu,
                    Kottarakara and Sakthikulangara, a Classical Dance
                    Studio in Kollam, a chartered accountant&apos;s
                    office in Chinnakada, a 12,000 sq.ft ceramics
                    showroom in Perumbavoor, a community-scale design
                    study, a neighbourhood park in Gandhi Nagar and a
                    jewellery showroom in Ezhukone. Every brief gets
                    the same site-first thinking and honest,
                    long-lived materials.
                </p>
            </FadeUp>

            {/* Vertical stacked layout — one project per row, sequential order 1→9 */}
            <div
                data-testid="portfolio-masonry"
                className="mt-20 flex flex-col gap-6"
            >
                {PROJECTS.map((p, i) => (
                    <ProjectCard
                        key={p.title}
                        project={p}
                        index={i}
                        heightClass={MASONRY_HEIGHTS[i % MASONRY_HEIGHTS.length]}
                    />
                ))}
            </div>
        </section>
    );
}
